import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchMyWaitlistEntries,
  fetchProgramCapacity,
  isProgramFull,
  type MyWaitlistEntry,
  type ProgramCapacityStatus,
} from '../services/programWaitlist';

/** Query keys, shared so an enrollment or a waitlist change can refresh both (BHCS-124). */
export const PROGRAM_CAPACITY_KEY = 'program-capacity';
export const MY_WAITLIST_KEY = 'my-waitlist';

export interface ProgramSeats {
  /** Undefined for an uncapped program, and while loading. */
  status: ProgramCapacityStatus | undefined;
  /** No seat for a family that holds no offer. False for an uncapped program. */
  isFull: boolean;
  /** The signed-in parent's children in line for this program (waiting or offered). */
  myEntries: MyWaitlistEntry[];
  /** Children a seat is being held for: they may enroll even though the program reads full. */
  heldStudentIds: string[];
  /** Refetch counts and entries, e.g. after an enrollment was refused as full. */
  refresh: () => void;
}

/**
 * Seats and the parent's waitlist entries for one program. Does nothing for an uncapped program
 * (`capacity` null), so every page that renders it costs no extra request until a cap is set.
 */
export function useProgramSeats(
  programId: string | null | undefined,
  capacity: number | null | undefined,
  options: { includeMine?: boolean } = {},
): ProgramSeats {
  const queryClient = useQueryClient();
  const capped = !!programId && capacity != null;
  const includeMine = options.includeMine ?? true;

  const { data: statusById } = useQuery({
    queryKey: [PROGRAM_CAPACITY_KEY, programId],
    queryFn: () => fetchProgramCapacity([programId as string]),
    enabled: capped,
  });

  const { data: myEntries } = useQuery({
    queryKey: [MY_WAITLIST_KEY, programId],
    queryFn: () => fetchMyWaitlistEntries(programId as string),
    enabled: capped && includeMine,
  });

  return useMemo(() => {
    // Read only what an enabled query returned: an uncapped program has no seats to report.
    const status = capped && statusById instanceof Map ? statusById.get(programId) : undefined;
    const entries = capped && includeMine && Array.isArray(myEntries) ? myEntries : [];
    return {
      status,
      isFull: isProgramFull(status),
      myEntries: entries,
      heldStudentIds: entries.filter((e) => e.status === 'offered').map((e) => e.student_id),
      refresh: () => {
        void queryClient.invalidateQueries({ queryKey: [PROGRAM_CAPACITY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [MY_WAITLIST_KEY] });
      },
    };
  }, [capped, includeMine, programId, statusById, myEntries, queryClient]);
}

/**
 * Seat counts for every capped program in a list, for catalog badges. Only programs that have a
 * cap are asked about; with none capped this makes no request at all.
 *
 * The key carries each program's capacity, not just its id: editing a cap changes no id, so an
 * id-only key kept serving the old counts ("Full 1 / 1" after raising the cap to 2).
 */
export function useProgramsCapacity(
  programs: Array<{ id: string; capacity?: number | null }> | undefined,
): Map<string, ProgramCapacityStatus> {
  const capped = useMemo(
    () => (programs ?? [])
      .filter((p) => p.capacity != null)
      .map((p) => `${p.id}:${p.capacity}`)
      .sort(),
    [programs],
  );
  const cappedIds = useMemo(() => capped.map((key) => key.slice(0, key.lastIndexOf(':'))), [capped]);
  const { data } = useQuery({
    queryKey: [PROGRAM_CAPACITY_KEY, 'list', capped],
    queryFn: () => fetchProgramCapacity(cappedIds),
    enabled: cappedIds.length > 0,
  });
  return cappedIds.length > 0 && data instanceof Map ? data : EMPTY_CAPACITY;
}

const EMPTY_CAPACITY = new Map<string, ProgramCapacityStatus>();
